/* Hallmark · genre: editorial · macrostructure: Split Studio · theme: Garden
 * (project palette, preserved) · enrichment: none (typography only)
 * type: Fraunces (display, opsz auto · SOFT 24 · WONK 0; `type-wonk` = SOFT 40 ·
 *       WONK 1, display sizes only) + Public Sans (body)
 * nav: N6 Masthead (issue-line=left, wordmark=centred, rule=single)
 * footer: Ft6 Letter close (page band; shared <FooterSection> untouched)
 * H2 hero knobs: ratio=5/7, right=proof column, divider=negative space
 * tone: editorial · anchor hue: warm (terracotta ~3%)
 */
import { createFileRoute } from '@tanstack/react-router';
import { Seo, JsonLd } from '@/components/seo';
import { useScrollDepth } from '@/hooks/use-scroll-depth';
import { useSectionViews } from '@/hooks/use-section-views';
import FooterSection from '../components/footer';
import { LandingHeader } from '../components/land-monitoring/landing-header';
import { Hero } from '../components/land-monitoring/hero';
import { HeroBackdrop } from '../components/land-monitoring/hero-backdrop';
import { SampleReport } from '../components/land-monitoring/sample-report';
import { TrustStrip } from '../components/land-monitoring/trust-strip';
import { Problems } from '../components/land-monitoring/problems';
import { HowItWorks } from '../components/land-monitoring/how-it-works';
import { Regions } from '../components/land-monitoring/regions';
import { Pricing } from '../components/land-monitoring/pricing';
import { Testimonials } from '../components/land-monitoring/testimonials';
import { Faqs } from '../components/land-monitoring/faqs';
import { FinalCta } from '../components/land-monitoring/final-cta';
import { StickyCta } from '../components/land-monitoring/sticky-cta';
import { LAND_MONITORING_DESCRIPTION } from '@/lib/land-monitoring-seo';

export const Route = createFileRoute('/land-monitoring')({
  component: LandMonitoringPage,
});

// Section ids tracked for engagement (match the anchor ids the nav scrolls to).
const TRACKED_SECTIONS = ['sample-report', 'how-it-works', 'coverage', 'pricing', 'faq'];

// Structured data so search engines and AI crawlers can resolve "Boundr" as a
// named entity offering a property-inspection service, not just an anonymous page.
const JSON_LD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': 'https://boundr.in/#organization',
      name: 'Boundr',
      url: 'https://boundr.in',
      description:
        'Boundr sends trained inspectors to vacant plots in India and reports back with photo and video evidence, encroachment alerts, and 5-day turnaround. Built for NRI and remote landowners.',
      sameAs: [
        'https://www.linkedin.com/company/boundr-in', // update once profile is live
      ],
    },
    {
      '@type': 'Service',
      serviceType: 'Land monitoring and plot inspection',
      provider: { '@id': 'https://boundr.in/#organization' },
      areaServed: ['Hyderabad', 'Bangalore', 'Vijayawada', 'Guntur', 'Amaravati', 'Visakhapatnam'],
      audience: {
        '@type': 'Audience',
        audienceType: 'NRI and remote landowners',
      },
      url: 'https://boundr.in/land-monitoring',
      offers: {
        '@type': 'Offer',
        price: '1499',
        priceCurrency: 'INR',
        description:
          'A full inspection report with photo and video evidence, starting at ₹1499 per visit.',
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://boundr.in/' },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Land Monitoring',
          item: 'https://boundr.in/land-monitoring',
        },
      ],
    },
    {
      '@type': 'WebPage',
      '@id': 'https://boundr.in/land-monitoring#webpage',
      url: 'https://boundr.in/land-monitoring',
      name: 'Land Monitoring & Plot Inspection | Boundr',
      description: LAND_MONITORING_DESCRIPTION,
      isPartOf: { '@id': 'https://boundr.in/#organization' },
    },
  ],
};

function LandMonitoringPage() {
  useScrollDepth('/land-monitoring');
  useSectionViews(TRACKED_SECTIONS, '/land-monitoring');

  // Brand design tokens (paper/ink/pine/clay/line/surface, font-display) are
  // global utilities defined in index.css (light and dark values included).
  return (
    <div className="bg-background text-foreground antialiased selection:bg-ring/25">
      <Seo
        title="Land Monitoring | Telangana & Andhra Pradesh | Boundr"
        description={LAND_MONITORING_DESCRIPTION}
        path="/land-monitoring"
        image="/og-land-monitoring.jpg"
        imageWidth={1200}
        imageHeight={630}
        hreflang={[
          { hreflang: 'en-IN', href: 'https://boundr.in/land-monitoring' },
          { hreflang: 'en', href: 'https://boundr.in/land-monitoring' },
          { hreflang: 'x-default', href: 'https://boundr.in/land-monitoring' },
        ]}
      />
      <JsonLd data={JSON_LD} id="land-monitoring-organization-service" />
      {/* isolate + relative so <HeroBackdrop> can span the masthead and the hero
          together — the header is in-flow above <main>, so the image can't be
          owned by <Hero> itself. */}
      <div className="relative isolate">
        <HeroBackdrop />
        <LandingHeader />
      </div>
      <main>
        <Hero />
        <TrustStrip />
        <SampleReport />
        <Problems />
        <HowItWorks />
        <Regions />
        <Pricing />
        <Testimonials />
        <Faqs />
        <FinalCta />
      </main>
      <FooterSection />
      <StickyCta />
    </div>
  );
}
